/**
 * Bilingual publishing guard — US-A02 AC6.
 *
 * "Given a required bilingual field with only one language filled, when I try
 * to publish, then publishing is blocked with a clear message."
 *
 * Arabic is the source language (docs/03 §4.1: the client falls back to Arabic
 * when the French value is empty), so a draft may legitimately have Arabic only.
 * Publication is where both are required.
 */
import { t } from '@/providers/i18n-provider'

export interface BilingualRule {
  /** Base name, e.g. `name` for `name_ar` / `name_fr`. */
  field: string
  /** Human label used in the blocking message. */
  label: string
}

export interface PublishCheckResult {
  ok: boolean
  /** Base field names missing their French value. */
  missingFr: string[]
  /** Base field names missing their Arabic value. */
  missingAr: string[]
  message?: string
}

const isEmpty = (value: unknown): boolean =>
  value === null || value === undefined || String(value).trim() === ''

export function checkBilingualForPublish(
  values: Record<string, unknown>,
  rules: BilingualRule[],
): PublishCheckResult {
  const missingFr: string[] = []
  const missingAr: string[] = []

  for (const rule of rules) {
    if (isEmpty(values[`${rule.field}_fr`])) missingFr.push(rule.label)
    if (isEmpty(values[`${rule.field}_ar`])) missingAr.push(rule.label)
  }

  const ok = missingFr.length === 0 && missingAr.length === 0
  if (ok) return { ok, missingFr: [], missingAr: [] }

  const parts: string[] = []
  if (missingAr.length > 0)
    parts.push(t('admin.publishGuard.arabic', { fields: missingAr.join(', ') }))
  if (missingFr.length > 0)
    parts.push(t('admin.publishGuard.french', { fields: missingFr.join(', ') }))

  return {
    ok,
    missingFr,
    missingAr,
    message: t('admin.publishGuard.blocked', {
      parts: parts.join(t('admin.publishGuard.separator')),
    }),
  }
}
